import * as React from 'react';
import type { GatsbyBrowser, WrapPageElementBrowserArgs } from 'gatsby';
import { getCookieConsentValue } from 'react-cookie-consent';
import './src/styles/global.css';

import 'prismjs/themes/prism-okaidia.css';

import CookieBanner from './src/components/common/cookie-banner/CookieBanner';
import {
  decideLandingEntry,
  recordLandingEntry,
} from './src/hooks/use-came-from-site';
import {
  GTM_COOKIE_NAME,
  CLARITY_COOKIE_NAME,
  COOKIE_ACCEPT,
} from './src/utils/cookie-consts';
import { ARTICLE_COMMENTS_SECTION_ID } from './src/utils/consts';

function clarityLoad(c, l, a, r, i, t, y) {
  c[a] =
    c[a] ||
    function () {
      (c[a].q = c[a].q || []).push(arguments);
    };
  t = l.createElement(r);
  t.async = 1;
  t.src = 'https://www.clarity.ms/tag/' + i;
  y = l.getElementsByTagName(r)[0];
  y.parentNode.insertBefore(t, y);
}

function googleTagManagerLoad(w, d, s, l, i) {
  w[l] = w[l] || [];
  w[l].push({ 'gtm.start': new Date().getTime(), event: 'gtm.js' });
  const f = d.getElementsByTagName(s)[0],
    j = d.createElement(s),
    dl = l != 'dataLayer' ? '&l=' + l : '';
  j.async = true;
  j.src = 'https://www.googletagmanager.com/gtm.js?id=' + i + dl;
  f.parentNode.insertBefore(j, f);
}

const loadAnalyticsScripts = () => {
  if (process.env.NODE_ENV === 'staging') return;

  const gtmConsent = getCookieConsentValue(GTM_COOKIE_NAME);
  const clarityConsent = getCookieConsentValue(CLARITY_COOKIE_NAME);

  if (clarityConsent === COOKIE_ACCEPT) {
    clarityLoad(
      window,
      document,
      'clarity',
      'script',
      process.env.GATSBY_CLARITY_ID,
    );
  }
  if (gtmConsent === COOKIE_ACCEPT) {
    googleTagManagerLoad(
      window,
      document,
      'script',
      process.env.GATSBY_GOOGLE_TM_DLN,
      process.env.GATSBY_GOOGLE_TM_ID,
    );
  }
};

const handleMouseMove = () => {
  loadAnalyticsScripts();
  window.removeEventListener('mousemove', handleMouseMove);
  window.removeEventListener('touchstart', handleMouseMove);
};

export const onInitialClientRender = () => {
  if (process.env.NODE_ENV === 'production') {
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('touchstart', handleMouseMove);
  }
};

type CustomPerfomanceEntry = PerformanceEntry & {
  serverTiming: { name: string; description: string }[];
};

export const wrapPageElement = ({ element }: WrapPageElementBrowserArgs) => {
  const nav = performance?.getEntriesByType(
    'navigation',
  )[0] as CustomPerfomanceEntry;

  const entry = nav?.serverTiming?.find((e) => e?.name === 'viewer-country');

  const country = entry ? entry.description : 'XX';
  return (
    <>
      {element}
      {(process.env.NODE_ENV === 'production' ||
        process.env.NODE_ENV === 'staging') && (
        <CookieBanner
          reloadAnalytics={loadAnalyticsScripts}
          country={country}
        />
      )}
    </>
  );
};

// Gatsby's canonical-URL replace can wipe history.state between these hooks.
export const onClientEntry: GatsbyBrowser['onClientEntry'] = decideLandingEntry;
export const onRouteUpdate: GatsbyBrowser['onRouteUpdate'] = recordLandingEntry;

export const shouldUpdateScroll: GatsbyBrowser['shouldUpdateScroll'] = ({
  routerProps: { location },
}) => {
  if (location.hash === `#${ARTICLE_COMMENTS_SECTION_ID}`) return true;

  setTimeout(() => {
    window.scrollTo(0, 0);
  }, 0);

  return false;
};
